import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Overline, Section, Pill } from "../components/Bits";
import { FRANCHISE_FORMATS, BRAND } from "../lib/gomill-data";
import { ArrowRight, Check, Coins, HandCoins, ScrollText, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: Sparkles, t: "Purpose-Driven Brand", d: "Join a movement, not just a business." },
  { icon: HandCoins, t: "End-to-End Support", d: "From licensing to training, we help you launch fast." },
  { icon: ScrollText, t: "Curated Millet Menu", d: "Chef-developed, central kitchen powered, easy to run." },
  { icon: Coins, t: "Scalable Opportunity", d: "Three formats. Multiple locations. Future-ready." },
];

const initial = { full_name: "", email: "", phone: "", preferred_format: "Kiosk", city: "", agree: true };

export default function Franchise() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry/franchise`, form);
      toast.success("Thanks! Our partnership team will reach out within 48 hours.");
      setForm(initial);
    } catch {
      toast.error("Couldn't submit. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Overline className="mb-4">Partner with GoMill</Overline>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-gomill-green leading-[1.02] tracking-tight">
              Bring GoMill to <span className="italic">your city.</span>
            </h1>
            <p className="mt-5 text-lg text-gomill-muted max-w-xl">Join India's fastest-growing millet food movement. Flexible formats. Proven model. Meaningful impact.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Pill tone="leaf">Proven Model</Pill>
              <Pill tone="mustard">Full Support</Pill>
              <Pill tone="cream">3 Formats</Pill>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[5/4] rounded-[32px] overflow-hidden bg-gomill-cream">
              <img src={BRAND.truckCity} alt="GoMill truck" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </Section>

      {/* Formats */}
      <Section className="bg-gomill-cream rounded-3xl mx-3 md:mx-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Overline className="mb-3">Choose your format</Overline>
          <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">Kiosk. Truck. Double-Decker.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {FRANCHISE_FORMATS.map((f) => (
            <div key={f.id} className="bg-white rounded-3xl border border-gomill-border overflow-hidden hover:-translate-y-1 transition-all" data-testid={`franchise-card-${f.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-gomill-cream">
                <img src={f.image} alt={f.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-gomill-green">{f.name}</h3>
                <p className="text-sm text-gomill-muted mt-2">{f.desc}</p>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gomill-cream rounded-2xl p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gomill-leaf font-bold">Footprint</div>
                    <div className="font-semibold text-gomill-green">{f.footprint}</div>
                  </div>
                  <div className="bg-gomill-cream rounded-2xl p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gomill-leaf font-bold">Investment</div>
                    <div className="font-semibold text-gomill-green">{f.invest}</div>
                  </div>
                </div>
                <a href="#franchise-form" className="mt-5 inline-flex items-center gap-2 text-gomill-green font-semibold hover:text-gomill-leaf">
                  Enquire Now <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Benefits */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b) => (
            <div key={b.t} className="p-6 rounded-3xl bg-white border border-gomill-border">
              <div className="w-12 h-12 rounded-2xl bg-gomill-mustard/30 text-gomill-green flex items-center justify-center mb-4">
                <b.icon className="w-5 h-5" />
              </div>
              <div className="font-serif text-xl text-gomill-green">{b.t}</div>
              <p className="text-sm text-gomill-muted mt-2">{b.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Form */}
      <Section id="franchise-form">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-3">Let's build together</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">A healthier future, on every street.</h2>
            <p className="mt-4 text-gomill-muted max-w-md">Drop us a note. Our partnership team will get in touch with a tailored proposal.</p>
            <ul className="mt-6 space-y-3 text-sm text-gomill-text">
              {["Pan-India brand support", "Centralised supply chain", "Marketing + training", "Tech, POS and ops playbook"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-gomill-leaf" /> {t}</li>
              ))}
            </ul>
          </div>
          <form onSubmit={submit} className="lg:col-span-7 bg-white border border-gomill-border rounded-3xl p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="franchise-form">
            <Field label="Full Name" name="full_name" value={form.full_name} onChange={update("full_name")} required />
            <Field label="Email Address" name="email" type="email" value={form.email} onChange={update("email")} required />
            <Field label="Phone Number" name="phone" value={form.phone} onChange={update("phone")} required />
            <SelectField label="Preferred Format" name="preferred_format" value={form.preferred_format} onChange={update("preferred_format")} options={["Kiosk", "Single-Tier Truck", "Double-Decker"]} />
            <Field label="City" name="city" value={form.city} onChange={update("city")} required />
            <label className="md:col-span-2 flex items-start gap-3 text-sm text-gomill-muted">
              <input type="checkbox" checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} data-testid="franchise-agree" className="mt-1 accent-gomill-green" />
              I agree to be contacted about franchise opportunities and accept the terms.
            </label>
            <button type="submit" disabled={loading || !form.agree} data-testid="franchise-submit-btn"
              className="md:col-span-2 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gomill-mustard text-gomill-green hover:bg-gomill-mustardDark font-bold disabled:opacity-60">
              {loading ? "Submitting…" : <>I'm Interested <ArrowRight className="w-4 h-4"/></>}
            </button>
          </form>
        </div>
      </Section>
    </>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest font-bold text-gomill-leaf mb-2">{label}</span>
      <input data-testid={`field-${props.name}`} {...props}
        className="w-full rounded-2xl border border-gomill-border bg-gomill-cream/60 px-4 py-3 text-gomill-green focus:outline-none focus:ring-2 focus:ring-gomill-mustard" />
    </label>
  );
}
function SelectField({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest font-bold text-gomill-leaf mb-2">{label}</span>
      <select data-testid={`field-${props.name}`} {...props}
        className="w-full rounded-2xl border border-gomill-border bg-gomill-cream/60 px-4 py-3 text-gomill-green focus:outline-none focus:ring-2 focus:ring-gomill-mustard">
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}
